/* eslint-disable sonarjs/cognitive-complexity */
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  PAGE_TYPES,
  VIP_QUALIFIERS_PAGE_TYPE,
  SHOW_PRICE_TYPES,
  DEAL_PAGE_PATH_QUALIFIERS,
} from '../../../brands/default/constants';
import { showEstimated } from '../../../helpers/adminFee';
import { isCreditecDeal } from '../../../helpers/creditec';
import {
  showPriceType,
  splatPriceDisplayDiscountAndOriginalPrice,
  splatPriceDisplayEstimatedText,
} from '../../../helpers/deals';
import { parseWowcherPath } from '../../../helpers/url';
import NormalPriceSplat from './NormalPriceSplat';
import VipDealPriceSplat from './VipDealPriceSplat';

const DealPriceSplat = ({
  className,
  coinFlipAnimation = false,
  columnSelection,
  deal = {},
  discount = false,
  forcePrice = true,
  isHideIfNoDiscount = true,
  isHideSplat = false,
  isProductPage,
  isTwoByTwo = false,
  isPPVisible = false,
  isNationalDeal = false,
  isMainDeal = false,
  rowSelection,
  secondCheckout,
  visible = true,
}) => {
  const [showVipSplatComponent, setShowVipSplatComponent] = useState(false);
  const [estimatedText, setEstimatedText] = useState(null);
  const [displayEstimatedText, setDisplayEstimatedText] = useState(false);
  const [displayDiscountAndOriginalPrice, setDisplayDiscountAndOriginalPrice] =
    useState(false);
  const [isVipUser, basketTotal] = useSelector((state) => [
    state?.user?.userprofile?.vipUser,
    state?.user?.basketTotal,
  ]);
  const forceSplat = isNationalDeal && isPPVisible;
  const router = useRouter();
  const path = router.asPath || '';
  const pathUrl = parseWowcherPath(path);

  const isVipHubPage = VIP_QUALIFIERS_PAGE_TYPE.includes(pathUrl.pageType);
  const isDealPage = DEAL_PAGE_PATH_QUALIFIERS.includes(pathUrl.pageType);
  const isSearchPageVipFilter =
    PAGE_TYPES.SEARCH.includes(pathUrl.pageType) && router.query?.vip;
  const isMarketingCreditDeal = deal?.display?.vipDiscountAllowed;
  const isVipExclusiveDeal = deal?.display?.vipExclusive;
  const isEarlyBirdPage = PAGE_TYPES.EARLY_BIRD.includes(pathUrl.pageType);
  // Show Vip Exclusive Graphic only on Deal pages, Early Bird Vip Hub and
  // search page only when vipSearch Filter is on.
  const showVipSplat =
    isVipUser &&
    (isMarketingCreditDeal ||
      isVipExclusiveDeal ||
      isEarlyBirdPage ||
      isVipHubPage) &&
    (isSearchPageVipFilter || isVipHubPage || isDealPage || isEarlyBirdPage);

  const priceType = showPriceType(
    deal,
    discount,
    forcePrice,
    isHideIfNoDiscount,
  );

  useEffect(() => {
    setEstimatedText(
      showEstimated({
        finalOrderAmount: basketTotal?.adminFeeApplicableOrderAmount || 0,
        price: deal.price,
        totalAdminFee: basketTotal?.totalAdminFee || 0,
      }),
    );
  }, [basketTotal, deal]);
  useEffect(() => {
    setDisplayEstimatedText(splatPriceDisplayEstimatedText({ deal, path }));
    setDisplayDiscountAndOriginalPrice(
      splatPriceDisplayDiscountAndOriginalPrice({ deal, isTwoByTwo, path }),
    );
  }, [deal, path]);

  useEffect(() => {
    if (showVipSplat && visible) {
      if (coinFlipAnimation) {
        const timeoutId = setTimeout(() => {
          setShowVipSplatComponent(true);
        }, 1_500);

        return () => clearTimeout(timeoutId);
      } else {
        setShowVipSplatComponent(true);
      }
    } else {
      setShowVipSplatComponent(false);
    }

    return null;
  }, [showVipSplat, visible, coinFlipAnimation]);
  // hide splat
  if (
    priceType === SHOW_PRICE_TYPES.hidden ||
    isHideSplat ||
    isCreditecDeal(deal) ||
    (displayDiscountAndOriginalPrice && !deal?.discount && !forceSplat)
  )
    return null;

  return (
    <div className="card-spin">
      {showVipSplatComponent ? (
        <div className="card-back">
          <VipDealPriceSplat
            basketTotal={basketTotal}
            className={className}
            coinFlipAnimation={coinFlipAnimation}
            deal={deal}
            discount={discount}
            displayDiscountAndOriginalPrice={displayDiscountAndOriginalPrice}
            displayEstimatedText={displayEstimatedText}
            estimatedText={estimatedText}
            forcePrice={forcePrice}
            isHideIfNoDiscount={isHideIfNoDiscount}
            isHideSplat={isHideSplat}
            isMainDeal={isMainDeal}
            isNationalDeal={isNationalDeal}
            isPPVisible={isPPVisible}
            isProductPage={isProductPage}
            isTwoByTwo={isTwoByTwo}
            showVipSplatComponent={showVipSplatComponent}
            visible={visible}
          />
        </div>
      ) : (
        <div className="card-front">
          <NormalPriceSplat
            basketTotal={basketTotal}
            className={className}
            coinFlipAnimation={coinFlipAnimation}
            columnSelection={columnSelection}
            deal={deal}
            discount={discount}
            displayDiscountAndOriginalPrice={displayDiscountAndOriginalPrice}
            displayEstimatedText={displayEstimatedText}
            estimatedText={estimatedText}
            forcePrice={forcePrice}
            isHideIfNoDiscount={isHideIfNoDiscount}
            isHideSplat={isHideSplat}
            isMainDeal={isMainDeal}
            isNationalDeal={isNationalDeal}
            isPPVisible={isPPVisible}
            isProductPage={isProductPage}
            isTwoByTwo={isTwoByTwo}
            rowSelection={rowSelection}
            secondCheckout={secondCheckout}
            showVipSplat={showVipSplat}
            visible={visible}
          />
        </div>
      )}
      <style jsx>{`
        .card-spin {
          transition: 150ms;
          transform-origin: 0 0;
        }
      `}</style>
    </div>
  );
};

DealPriceSplat.propTypes = {
  className: PropTypes.string,
  coinFlipAnimation: PropTypes.bool,
  columnSelection: PropTypes.number,
  deal: PropTypes.object,
  discount: PropTypes.bool,
  forcePrice: PropTypes.bool,
  isHideIfNoDiscount: PropTypes.bool,
  isHideSplat: PropTypes.bool,
  isMainDeal: PropTypes.bool,
  isNationalDeal: PropTypes.bool,
  isPPVisible: PropTypes.bool,
  isProductPage: PropTypes.bool,
  isTwoByTwo: PropTypes.bool,
  rowSelection: PropTypes.number,
  secondCheckout: PropTypes.bool,
  visible: PropTypes.bool,
};

export default DealPriceSplat;
